import React, {useEffect, useRef, useState} from 'react';
import styles from './UpdateScopePopUp.module.css';
import {getDocumentAvailableScopesAPI, updateScopesAPI} from '../../../services/admin';
import {XMarkIcon} from '@heroicons/react/24/outline';
import {AvailableScopeResponseData} from "../../../types/admin";

interface UpdateScopePopUpProps {
    isOpen: boolean;
    documentId: number;
    onClose: () => void;
}

//클라이언트 회사명 초성 스크롤
const getInitialConsonant = (str: string): string => {
    const char = str.charAt(0);
    const consonants = [
        'ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ',
        'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'
    ];
    if (/[가-힣]/.test(char)) {
        const code = char.charCodeAt(0) - 0xAC00;
        const consonantIndex = Math.floor(code / 28 / 21);
        return consonants[consonantIndex];
    }
    if (/[A-Za-z]/.test(char)) {
        return char.toUpperCase();
    }
    if (/[0-9]/.test(char)) {
        return '#';
    }
    return char;
};

//공개 대상 클라이언트 수정 팝업
const UpdateScopePopUp: React.FC<UpdateScopePopUpProps> = ({
                                                               isOpen, documentId, onClose
                                                           }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [clients, setClients] = useState<AvailableScopeResponseData[]>([]);
    const [selectedClients, setSelectedClients] = useState<number[]>([]);
    const [loading, setLoading] = useState(true);
    const clientListRef = useRef<HTMLDivElement>(null);

    //공개 대상 조회
    useEffect(() => {
        const fetchClients = async () => {
            try {
                //현재 공개 대상 리스트 API, 클라이언트 목록과 각 클라이언트에게 공개된 상태인지 반환
                const response = await getDocumentAvailableScopesAPI(documentId);
                //회사명 정렬
                const sortedClients = response.sort((a, b) =>
                    a.companyName.localeCompare(b.companyName, 'ko-KR')
                );
                setClients(sortedClients);
                //공개 대상 클라이언트 목록
                setSelectedClients(sortedClients.filter(client => client.isSelected).map(client => client.clientId));
            } catch (error) {
                console.error('Error fetching clients:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchClients();
    }, [documentId]);

    //체크박스 선택
    const handleCheckboxChange = (clientId: number) => {
        setSelectedClients(prev => {
            if (prev.includes(clientId)) {
                return prev.filter(id => id !== clientId);
            } else {
                return [...prev, clientId];
            }
        });
    };

    //수정
    const handleSubmit = async () => {
        //공개 대상 클라이언트 수정 API
        await updateScopesAPI({
            documentId,
            clientIds: selectedClients
        });
        onClose();
    };

    //회사명 첫 초성
    const getFirstIndexForChar = (char: string) => {
        return filteredClients.findIndex(client => {
            const initialChar = getInitialConsonant(client.companyName);
            if (char === '#') {
                return /[0-9]/.test(client.companyName[0]);
            }
            if (char === 'A') {
                return /[A-Za-z]/.test(client.companyName[0]);
            }
            return initialChar === char;
        });
    };

    //현재 존재하는 회사명 초성 목록
    const getAvailableChars = () => {
        const chars = new Set<string>();
        filteredClients.forEach(client => {
            let initial;
            if (/[A-Za-z]/.test(client.companyName[0])) {
                initial = client.companyName[0].toUpperCase();
            } else if (/[0-9]/.test(client.companyName[0])) {
                initial = '#';
            } else {
                initial = getInitialConsonant(client.companyName);
            }
            chars.add(initial);
        });

        return Array.from(chars).sort((a, b) => {
            if (a === '#') return -1;
            if (b === '#') return 1;
            return a.localeCompare(b, 'ko-KR');
        });
    };

    //초성 스크롤 선택 시 스크롤 이동
    const handleLetterClick = (char: string) => {
        const index = getFirstIndexForChar(char);
        if (index !== -1 && clientListRef.current) {
            const itemHeight = 40;
            clientListRef.current.scrollTop = index * itemHeight;
        }
    };

    //검색
    const filteredClients = clients.filter(client =>
        client.companyName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const availableChars = getAvailableChars();

    if (!isOpen) return null;

    if (loading) {
        return <div className={styles.loading}>Loading...</div>;
    }

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.header}>
                    <h2>공개 대상 클라이언트</h2>
                    {/*닫기 버튼*/}
                    <button onClick={onClose} className={styles.closeButton}>
                        <XMarkIcon className={styles.closeIcon}/>
                    </button>
                </div>

                <div className={styles.searchContainer}>
                    <div className={styles.checkboxLabel}>
                        {/*클라이언트 전체 선택 체크 박스*/}
                        <input
                            type="checkbox"
                            checked={selectedClients.length === clients.length}
                            onChange={() => {
                                if (selectedClients.length === clients.length) {
                                    setSelectedClients([]);
                                } else {
                                    setSelectedClients(clients.map(client => client.clientId));
                                }
                            }}
                            className={styles.checkbox}
                        />
                        <span>클라이언트</span>
                    </div>
                    {/*클라이언트 검색*/}
                    <input
                        type="text"
                        placeholder="검색"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.searchInput}
                    />
                </div>

                <div className={styles.clientListContainer}>
                    <div ref={clientListRef} className={styles.clientList}>
                        {filteredClients.map((client) => (
                            <label key={client.clientId} className={styles.clientItem}>
                                {/*체크박스 단일 선택*/}
                                <input
                                    type="checkbox"
                                    checked={selectedClients.includes(client.clientId)}
                                    onChange={() => handleCheckboxChange(client.clientId)}
                                    className={styles.checkbox}
                                />
                                <span>{client.companyName}</span>
                            </label>
                        ))}
                    </div>
                    <div className={styles.alphabet}>
                        {/*스크롤*/}
                        {availableChars.map((char) => (
                            <div
                                key={char}
                                className={styles.letter}
                                onClick={() => handleLetterClick(char)}
                            >
                                {char}
                            </div>
                        ))}
                    </div>
                </div>

                {/*공개 대상 수정 버튼*/}
                <button onClick={handleSubmit} className={styles.submitButton}>
                    수정
                </button>
            </div>
        </div>
    );
};

export default UpdateScopePopUp;